import { JsonViewer } from '@/components/JsonViewer';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { formatDateTime } from '@/util/dates';
import type { ChatConversation, ChatMessage } from '@rock/types';
import { ChevronRight, Wrench } from 'lucide-react';
import { type FC, useState } from 'react';

/**
 * Renders a conversation's embedded messages: user messages right-aligned, assistant left-aligned,
 * tool calls/results as collapsible audit blocks between them.
 */
export const ChatTranscript: FC<{ conversation: ChatConversation }> = ({ conversation }) => {
    if (conversation.messages.length === 0) {
        return <div className="section-description">No messages.</div>;
    }
    return (
        <div className="flex flex-col gap-3">
            {conversation.messages.map((message) => (
                <TranscriptMessage
                    key={message.id}
                    message={message}
                    committedDraftIds={conversation.committedDraftIds}
                />
            ))}
        </div>
    );
};

const TranscriptMessage: FC<{ message: ChatMessage; committedDraftIds: ChatConversation['committedDraftIds'] }> = ({
    message,
    committedDraftIds,
}) => {
    switch (message.role) {
        case 'user':
        case 'assistant': {
            const isUser = message.role === 'user';
            return (
                <div className={cn('flex flex-col gap-1', isUser ? 'items-end' : 'items-start')}>
                    <div
                        className={cn(
                            'max-w-[75%] whitespace-pre-wrap rounded-lg px-4 py-2 text-sm',
                            isUser ? 'bg-primary text-primary-foreground' : 'border bg-muted/50',
                        )}
                    >
                        {message.content || <span className="italic opacity-70">(empty)</span>}
                    </div>
                    <span className="text-xs text-muted-foreground">
                        {isUser ? 'User' : 'Assistant'} · {formatDateTime(message.createdAt)}
                    </span>
                </div>
            );
        }
        case 'tool-call':
            return (
                <ToolBlock
                    label={`Tool call: ${message.toolCall?.toolName ?? 'unknown'}`}
                    data={message.toolCall?.args}
                    committedAt={undefined}
                />
            );
        case 'tool-result':
            return (
                <ToolBlock
                    label={`Tool result: ${message.toolResult?.toolName ?? 'unknown'}`}
                    data={message.toolResult?.result}
                    committedAt={committedDraftIds[message.id]}
                />
            );
    }
};

const ToolBlock: FC<{ label: string; data: unknown; committedAt: string | undefined }> = ({
    label,
    data,
    committedAt,
}) => {
    const [open, setOpen] = useState(false);
    return (
        <div className="rounded-md border border-dashed">
            <button
                type="button"
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-muted-foreground hover:bg-muted/50"
                onClick={() => setOpen((wasOpen) => !wasOpen)}
            >
                <ChevronRight className={cn('h-3.5 w-3.5 transition-transform', open && 'rotate-90')} />
                <Wrench className="h-3.5 w-3.5" />
                <span className="font-medium">{label}</span>
                {committedAt && (
                    <Badge variant="secondary" className="ml-auto">
                        Draft committed
                    </Badge>
                )}
            </button>
            {open && (
                <div className="border-t px-3 py-2">
                    {data === undefined ? <div className="field-hint">No payload.</div> : <JsonViewer data={data} />}
                </div>
            )}
        </div>
    );
};
